import React from 'react';

import { Anchor, Group, Switch, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconEdit, IconTrashX } from '@tabler/icons-react';
import HelpButton from 'components/HelpButton';
import Menu from 'components/Menu';
import Table from 'components/Table';
import useGLMKiloFlows from 'hooks/glmFlows/useGLMKiloFlows';
import useUpdateGLMKiloFlow from 'hooks/glmFlows/useUpdateGLMKiloFlow';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { useNavigate } from 'react-router-dom';
import { CellProps } from 'react-table';
import { GLMKiloFlowList } from 'types';
import notificationHandler from 'utils/notifications';

import FlowsEmptyState from './FlowsEmptyState';
import ModalDeleteGLMKiloFlow from './ModalDeleteGLMKiloFlow';
import ModalGLMKiloRecords from './ModalGLMKiloRecords';
import {
  ACTION_LABELS,
  GLMKiloFlowActions,
  GLMKiloFlowTriggers,
  TRIGGER_DESCRIPTIONS,
  TRIGGER_LABELS,
} from '../../GLMKiloFlowBuilder/types';

const GLMKiloFlowTableContainer = (): JSX.Element => {
  const navigate = useNavigate();
  const { data, isLoading, isError, isSuccess, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGLMKiloFlows();
  const { mutate: updateFlow } = useUpdateGLMKiloFlow();
  const { trackGLMFlowToggled } = useStaffTracking();
  const [recordsFlow, setRecordsFlow] = React.useState<GLMKiloFlowList | null>(null);
  const [recordsModalOpened, { open: openRecordsModal, close: closeRecordsModal }] = useDisclosure(false);
  const [selectedFlow, setSelectedFlow] = React.useState<GLMKiloFlowList | null>(null);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const { successNotification, errorNotification } = notificationHandler();

  const handleEdit = (flow: GLMKiloFlowList): void => {
    navigate(`/integrations/glm/glm-builder/${flow.glmKiloFlowId}`);
  };

  const handleDelete = (flow: GLMKiloFlowList): void => {
    setSelectedFlow(flow);
    openDeleteModal();
  };

  const handleToggleEnabled = (flow: GLMKiloFlowList): void => {
    updateFlow(
      {
        glmKiloFlowId: flow.glmKiloFlowId,
        enabled: !flow.enabled,
        tcPipelineId: flow.tcPipelineId,
        tcStageId: flow.tcStageId,
        tcTag: flow.tcTag,
        acAthleteStatus: flow.acAthleteStatus,
      },
      {
        onSuccess: () => {
          trackGLMFlowToggled({
            flow_id: flow.glmKiloFlowId,
            direction: 'glm_to_kilo',
            trigger: flow.trigger,
            action: flow.action,
            enabled: !flow.enabled,
          });
          successNotification('Flow updated', `flow ${!flow.enabled ? 'enabled' : 'disabled'}.`);
        },
        onError: error => {
          errorNotification('Flow update failed', error.response?.data.message);
        },
      },
    );
  };

  const columns = [
    {
      Header: 'GLM Event',
      accessor: 'trigger' as const,
      Cell: ({ value }: { value: string }) => {
        const trigger = value as GLMKiloFlowTriggers;
        return (
          <Group spacing={4} noWrap>
            <Text>{TRIGGER_LABELS[trigger] ?? value?.replace(/_/g, ' ')}</Text>
            {TRIGGER_DESCRIPTIONS[trigger] && (
              <HelpButton width={260} position='top-start' withinPortal>
                <Text size='sm' style={{ whiteSpace: 'normal' }}>
                  {TRIGGER_DESCRIPTIONS[trigger]}
                </Text>
              </HelpButton>
            )}
          </Group>
        );
      },
    },
    {
      Header: 'Details',
      id: 'triggerConfig',
      Cell: ({ row }: CellProps<GLMKiloFlowList>) => {
        const { tcPipelineId, tcStageId, tcPipelineName, tcStageName, tcTag } = row.original;
        const trigger = row.original.trigger as GLMKiloFlowTriggers;
        if (trigger === GLMKiloFlowTriggers.PIPELINE_STAGE_MOVEMENT && (tcPipelineId || tcStageId)) {
          const label = [tcPipelineName ?? tcPipelineId, tcStageName ?? tcStageId]
            .filter(Boolean)
            .join(' → ');
          return <Text>{label || '-'}</Text>;
        }
        if (trigger === GLMKiloFlowTriggers.TAG_ADDED && tcTag) {
          return <Text>{tcTag}</Text>;
        }
        return <Text>-</Text>;
      },
    },
    {
      Header: 'Kilo Action',
      accessor: 'action' as const,
      Cell: ({ value }: { value: string }) => (
        <Text>{ACTION_LABELS[value as GLMKiloFlowActions] ?? value?.replace(/_/g, ' ')}</Text>
      ),
    },
    {
      Header: 'Details',
      id: 'actionConfig',
      Cell: ({ row }: CellProps<GLMKiloFlowList>) => <Text>{row.original.acAthleteStatus || '-'}</Text>,
    },
    {
      Header: 'Times Executed',
      accessor: 'timesExecuted' as const,
      Cell: ({ row }: CellProps<GLMKiloFlowList>) => (
        <Anchor
          onClick={() => {
            setRecordsFlow(row.original);
            openRecordsModal();
          }}
        >
          {row.original.timesExecuted}
        </Anchor>
      ),
    },
    {
      Header: 'Enabled',
      accessor: 'enabled' as const,
      Cell: ({ row }: CellProps<GLMKiloFlowList>) => (
        <Switch checked={row.original.enabled} onChange={() => handleToggleEnabled(row.original)} />
      ),
    },
    {
      id: 'actions',
      Cell: ({ row }: CellProps<GLMKiloFlowList>) => (
        <Group>
          <Menu
            items={[
              {
                title: 'Edit',
                onClick: () => handleEdit(row.original),
                icon: <IconEdit size={16} />,
              },
              {
                title: 'Delete',
                onClick: () => handleDelete(row.original),
                color: 'red',
                icon: <IconTrashX size={16} />,
              },
            ]}
          />
        </Group>
      ),
    },
  ];

  return (
    <>
      <Table
        tableData={
          data?.pages
            .map(page => page.data.results)
            .flat()
            .sort((a, b) => a.trigger.localeCompare(b.trigger)) || []
        }
        columns={data?.pages.map(page => page.data.results).flat().length ? columns : []}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage ?? false}
        fetchNextPage={fetchNextPage}
        EmptyState={
          <FlowsEmptyState
            description='Set one up to start syncing your GLM contacts to Kilo automatically.'
            onCreate={() => navigate('/integrations/glm/glm-builder')}
          />
        }
      />
      <ModalGLMKiloRecords
        opened={recordsModalOpened}
        onClose={closeRecordsModal}
        flowId={recordsFlow?.glmKiloFlowId ?? null}
        flowLabel={recordsFlow?.trigger}
      />
      {selectedFlow && (
        <ModalDeleteGLMKiloFlow
          opened={deleteModalOpened}
          handleClose={closeDeleteModal}
          flow={selectedFlow}
        />
      )}
    </>
  );
};

export default GLMKiloFlowTableContainer;
